import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const curriculum = [
  { title: 'Introduction to Large Language Models', duration: '22 min', lessons: 3 },
  { title: 'Understanding the ChatGPT Architecture', duration: '35 min', lessons: 4 },
  { title: 'The Art of Prompt Engineering', duration: '48 min', lessons: 6 },
  { title: 'Role Prompting & System Messages', duration: '30 min', lessons: 4 },
  { title: 'Chain-of-Thought & Few-Shot Prompting', duration: '42 min', lessons: 5 },
  { title: 'Building Custom GPTs (No Code)', duration: '55 min', lessons: 6 },
  { title: 'Advanced Prompt Patterns & Techniques', duration: '38 min', lessons: 4 },
  { title: 'Prompt Optimization & Evaluation', duration: '45 min', lessons: 5 },
  { title: 'Real-World Applications & Case Studies', duration: '40 min', lessons: 3 },
  { title: 'Capstone Project: Build Your AI Workflow', duration: '45 min', lessons: 2 },
];

const skills = [
  'Prompt Engineering', 'Chain-of-Thought Prompting', 'Few-Shot Learning',
  'Custom GPT Creation', 'System Message Design', 'AI Workflow Automation',
  'Prompt Optimization', 'Real-World LLM Applications',
];

export default function ChatGPTMasteryPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-accent-100 text-accent-800 text-xs">
                  Beginner
                </span>
                <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-secondary-100 text-secondary-900 text-xs">
                  ChatGPT
                </span>
              </div>

              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                ChatGPT & Prompt Engineering Mastery
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed mb-6">
                Master the art of prompting to get superhuman results from ChatGPT and other LLMs. From basic prompts to advanced chain-of-thought techniques — this course covers everything you need to become a prompt engineering expert.
              </p>

              <div className="flex flex-wrap items-center gap-5 text-sm text-foreground-600 mb-6">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  6 hours
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-book-open-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  42 lessons
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center text-primary-500" />
                  12,400 students
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-primary-400" />
                  4.9 (1,200+ reviews)
                </span>
              </div>

              <div className="flex items-center gap-4">
                <span className="text-3xl font-heading font-extrabold text-foreground-950">$49</span>
                <Link
                  to="/checkout?course=chatgpt-mastery"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-primary-500 text-background-50 hover:bg-primary-600 h-11 px-6 text-sm"
                >
                  Enroll Now
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            </div>

            <div className="lg:w-80 w-full shrink-0">
              <div className="rounded-xl overflow-hidden border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20AI%20chatbot%20interface%20with%20clean%20design%2C%20futuristic%20holographic%20chat%20bubbles%20floating%20on%20a%20pure%20white%20background%2C%20soft%20warm%20ambient%20lighting%2C%20editorial%20product%20photography%20style%2C%20sleek%20and%20professional%20aesthetic%2C%20subtle%20gradient%20accents%2C%20no%20text%2C%20clean%20composition&width=640&height=400&seq=chatgpt-course-hero&orientation=landscape"
                  alt="ChatGPT & Prompt Engineering Mastery"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Learn + Curriculum */}
      <section className="py-14 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Curriculum */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">Course Curriculum</h2>
              <div className="space-y-3">
                {curriculum.map((section, idx) => (
                  <div
                    key={section.title}
                    className="flex items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-full bg-background-100 text-foreground-500 text-xs font-semibold flex items-center justify-center shrink-0">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-950">{section.title}</p>
                        <p className="text-xs text-foreground-500">{section.lessons} lessons</p>
                      </div>
                    </div>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">{section.duration}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3">Skills You Will Gain</h3>
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((skill) => (
                    <span
                      key={skill}
                      className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 font-medium whitespace-nowrap bg-background-100 text-foreground-700 text-xs"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                <h3 className="font-heading font-bold text-foreground-950 mb-3">This Course Includes</h3>
                <ul className="space-y-2 text-sm text-foreground-600">
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    6 hours on-demand video
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    42 downloadable resources
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    1 capstone project
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Certificate of completion
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Lifetime access
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="ri-check-line text-accent-500 w-4 h-4 flex items-center justify-center" />
                    Community & Q&A access
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-14 bg-primary-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-background-50 mb-2">
            Start Your Prompt Engineering Journey
          </h2>
          <p className="text-sm text-primary-100 mb-5 max-w-md mx-auto">
            Join 12,400+ students. One-time payment, lifetime access.
          </p>
          <Link
            to="/checkout?course=chatgpt-mastery"
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-background-50 text-primary-600 hover:bg-background-100 h-10 px-5 text-sm"
          >
            Enroll for $49
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}